<template>
  <div
    :style="{ height: `${props.height}px`, width: `${props.width}px` }"
    class="relative flex items-center border border-[#E0E1E1] rounded-[4px] w-max"
  >
    <input
      :value="_amount"
      type="text"
      :placeholder="placeholder"
      @input="handleInput"
      @blur="handleBlur"
      @focus="handleFocus"
      :style="{ paddingRight: `${props.buttonSize * 2 + 24}px` }"
      class="absolute top-0 right-0 bottom-0 left-0 text-center border-none py-2 pl-3 rounded-[4px]"
    />
    <div class="absolute right-2 top-0 bottom-0 flex items-center space-x-2">
      <button
        :style="{ width: `${props.buttonSize}px`, height: `${props.buttonSize}px` }"
        class="flex items-center justify-center border border-rim-300 rounded"
        @click="decreaseAmount"
      >
        <div class="minus-icon"></div>
      </button>
      <button
        :style="{ width: `${props.buttonSize}px`, height: `${props.buttonSize}px` }"
        class="flex items-center justify-center border border-rim-300 rounded"
        @click="increaseAmount"
      >
        <div class="plus-icon"></div>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import Decimal from 'decimal.js'
import { computed } from 'vue'

let debounceTimer: ReturnType<typeof setTimeout> | null = null
const props = withDefaults(
  defineProps<{
    amount?: string | number | null
    placeholder?: string
    width?: number
    height?: number
    buttonSize?: number
    step?: number
    precision?: number
    min?: number
    max?: number
  }>(),
  {
    placeholder: '请输入限价金额',
    width: 160,
    height: 40,
    buttonSize: 24,
    step: 1,
    precision: 2,
    min: 0,
    max: 10000000,
  },
)

const emit = defineEmits(['input', 'focus', 'update:amount'])
const _amount = computed({
  get() {
    // 返回格式化的字符串以匹配 input.value
    const value = props.amount !== undefined && props.amount !== null ? props.amount : ''
    return typeof value === 'number' ? value.toString() : value
  },
  set(newValue: string) {
    // 解析为数字并发送
    const numericValue = newValue.trim() === '' ? undefined : parseFloat(newValue)
    console.log('Setting _amount to:', numericValue) // 调试
    emit('update:amount', numericValue)
  },
})

const handleInput = (event: Event) => {
  const input = event.target as HTMLInputElement
  let value = input.value.trim()

  if (debounceTimer) {
    clearTimeout(debounceTimer)
  }

  debounceTimer = setTimeout(() => {
    if (value === '') {
      input.value = value
    } else {
      value = value.replace(/[^0-9.-]/g, '')
      value = value.replace(/--+/g, '-')
      value = value.replace(/\.\.+/g, '.')

      const hasNegative = value.startsWith('-')
      value = value.replace('-', '')
      const hasDecimal = value.includes('.')
      const parts = value.split('.')
      let integerPart = parts[0].replace(/^0+(?!$)/, '') || '0'
      const decimalPart = parts[1]

      if (hasDecimal) {
        value = integerPart + '.' + (decimalPart || '')
      } else {
        value = integerPart
      }

      if (props.precision && hasDecimal) {
        const decimalLength = decimalPart ? decimalPart.length : 0
        if (decimalLength > props.precision) {
          value = parseFloat(value).toFixed(props.precision)
        }
      }

      if (hasNegative) {
        value = '-' + value
      }
    }
    input.value = value
    _amount.value = value // 触发 set，将数字发送给父组件
  }, 50)
}

const handleBlur = (event: Event) => {
  const input = event.target as HTMLInputElement
  const value = input.value.trim()

  if (value === '') {
    return
  }

  if (props.precision !== undefined) {
    const numericValue = parseFloat(value)
    if (!isNaN(numericValue)) {
      input.value = numericValue.toFixed(props.precision)
      _amount.value = input.value // 触发 set
    }
  }
}

const decreaseAmount = () => {
  const currentAmount = new Decimal(props.amount || props.min)
  const newAmount = currentAmount.minus(props.step)
  const clampedAmount = newAmount.lessThan(props.min) ? new Decimal(props.min) : newAmount
  _amount.value = clampedAmount.toFixed(props.precision)
  emit('input', parseFloat(_amount.value))
}

const increaseAmount = () => {
  const currentAmount = new Decimal(props.amount || props.min)
  const newAmount = currentAmount.plus(props.step)
  const clampedAmount = newAmount.greaterThan(props.max) ? new Decimal(props.max) : newAmount
  _amount.value = clampedAmount.toFixed(props.precision)
  emit('input', parseFloat(_amount.value))
}

const handleFocus = () => {
  emit('focus')
}
</script>

<style scoped>
/* 样式保持不变 */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
}

input:focus {
  border: 1px solid var(--color-primary-500);
  outline: none;
}

.plus-icon,
.minus-icon {
  position: relative;
  width: 100%;
  height: 100%;
}

.plus-icon::before,
.plus-icon::after,
.minus-icon::before {
  content: '';
  position: absolute;
  background-color: #363636;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.plus-icon::before {
  width: 60%;
  height: 2px;
}

.plus-icon::after {
  width: 2px;
  height: 60%;
}

.minus-icon::before {
  width: 60%;
  height: 2px;
}
</style>
